/**
 * El plan de suscripción, leído desde el backend y dicho en una sola voz.
 *
 * Tres pantallas hablan del plan —el panel, el muro de pago y la tab de Perfil—
 * y las tres tienen que decir el mismo precio, el mismo nombre y la misma fecha.
 * Cuando el precio vivía escrito a mano en cada una, el panel decía "$160 USD,
 * pago único" mientras el cobro real ya era trimestral.
 *
 * Desde septiembre de 2026 se vende UN plan: `MONTHLY`, hoy USD 30 al mes (fue
 * USD 18 en septiembre, sin suscripciones vivas a ese precio), con una
 * prueba de 7 días (con tarjeta) para quien nunca tuvo plan. El trimestral y los
 * de pago único siguen reconocidos para las cuentas que ya los tienen.
 *
 * `readPlan` acepta tanto la raíz de /dashboard como su `membership`: el backend
 * manda los mismos campos en los dos lugares (la raíz para este cliente,
 * `membership` para el hub viejo) y acá se prefiere la raíz sin romperse si un
 * despliegue quedó a mitad de camino.
 */

import { messages } from '~/i18n';
import { formatLongDate } from '~/lib/date';

/** Lo que cuesta. Un solo lugar: si cambia el precio, cambia acá. No se traduce. */
export const PLAN_PRICE = 'USD 30';
/** El mismo precio en centavos, para calcular el precio con descuento. */
export const PLAN_PRICE_CENTS = 3000;

/** "USD 23", "USD 22.50": un monto en centavos de USD dicho como el precio de lista. */
export function formatPlanCents(cents: number): string {
  const units = Math.max(0, cents) / 100;
  return `USD ${Number.isInteger(units) ? units : units.toFixed(2)}`;
}

/** Lo que hace falta de un descuento (cupón validado o el del socio) para calcular el precio. */
export interface PriceDiscount {
  amount_off?: number | null;
  percent_off?: number | null;
  currency?: string | null;
  /** Como en Stripe: `once`, `repeating` (con `duration_in_months`) o `forever`. */
  duration?: string | null;
  duration_in_months?: number | null;
}

/**
 * El precio mensual con el descuento, en centavos, o null si no hay uno que
 * se pueda calcular (monto en otra moneda, descuento vacío). Nunca negativo.
 *
 * Se muestra junto al de lista ANTES de Stripe, para que quien viene por un
 * socio vea su beneficio antes de sacar la tarjeta. Stripe sigue siendo quien
 * cobra; el número sale de la misma regla que aplica Stripe (monto fijo o
 * porcentaje sobre el precio).
 */
export function discountedPriceCents(discount: PriceDiscount | null | undefined): number | null {
  if (!discount) return null;
  const amount = Number(discount.amount_off);
  if (Number.isFinite(amount) && amount > 0) {
    if ((discount.currency ?? 'usd').toLowerCase() !== 'usd') return null;
    return Math.max(0, PLAN_PRICE_CENTS - amount);
  }
  const percent = Number(discount.percent_off);
  if (Number.isFinite(percent) && percent > 0) {
    return Math.max(0, Math.round(PLAN_PRICE_CENTS * (1 - percent / 100)));
  }
  return null;
}

/**
 * En cuántos meses pagados rige el descuento: un número, o `'forever'`.
 *
 * `repeating` dice cuántos; `once` es el primer mes. Sin `duration` (una
 * respuesta vieja o incompleta) también es el primer mes: nunca se promete un
 * precio rebajado para siempre sin que el servidor lo diga. El descuento del
 * socio es `repeating` 1 mes desde 2026-10-07: USD 23 el primer mes y después
 * el precio de lista.
 */
export function discountMonths(discount: PriceDiscount | null | undefined): number | 'forever' {
  if (discount?.duration === 'forever') return 'forever';
  const months = Number(discount?.duration_in_months);
  if (discount?.duration === 'repeating' && Number.isInteger(months) && months > 0) return months;
  return 1;
}

/**
 * La cadencia y los nombres dependen del idioma, así que son funciones que leen
 * el diccionario al llamarse (nunca al cargar el módulo: congelaría el idioma).
 */
export function planCadence(): string {
  return messages().plan.labels.cadence;
}

/** "USD 30 al mes". */
export function planPriceLabel(): string {
  return `${PLAN_PRICE} ${planCadence()}`;
}

/** Cómo se llama el plan mensual delante del candidato. */
export function monthlyLabel(): string {
  return messages().plan.labels.monthly(PLAN_PRICE);
}

/**
 * El precio con el que se renueva ESTE plan, o null si no es el que se vende hoy.
 * A quien sigue en el trimestral no se le dice "se renueva por USD 30 al mes":
 * su suscripción se renueva con su precio de siempre, que este cliente no conoce.
 */
export function planRenewalPrice(plan: string | null): string | null {
  return plan === 'MONTHLY' ? planPriceLabel() : null;
}

export interface PlanState {
  /** El código del plan tal como lo guarda el backend. Null si nunca compró. */
  plan: string | null;
  /** LA puerta: sólo con esto en true salen postulaciones a empleadores. */
  plan_active: boolean;
  /** Cuándo se renueva (o cuándo venció, si ya no está activo). */
  renews_at: string | null;
  /** Cuándo vio llegar su primera postulación de demostración. */
  demo_sent_at: string | null;
  /**
   * Nunca tuvo plan: el checkout le da 7 días gratis. Ausente = false: sin la
   * palabra del servidor no se promete una prueba que quizá no va a dar.
   */
  trial_eligible: boolean;
  /**
   * Qué llamado al plan corresponde, decidido por el servidor. Ausente (servidor
   * viejo) = null: `planCtaCase` lo deriva de `trial_eligible` y `plan`.
   */
  cta_case: 'trial' | 'reactivate' | 'activate' | null;
  /** Está en la prueba gratis (y el plan vale: puede enviar). */
  trialing: boolean;
  /** Cuándo termina la prueba y llega el primer cobro. */
  trial_ends_at: string | null;
  /**
   * Tiene cuenta de cobro en Stripe y puede abrir el portal de clientes para
   * cambiar la tarjeta o cancelar. Ausente = false: las cortesías y los pagos
   * viejos no tienen portal, y ahí se sigue diciendo "escríbenos".
   */
  can_manage_billing: boolean;
  /**
   * El descuento del socio que lo trajo (USD 7 sólo en el primer mes), o null.
   * El checkout lo aplica solo; esto es para que la oferta lo muestre antes.
   */
  partner_discount: PartnerDiscount | null;
  /**
   * El cobro falló (past_due): el plan sigue vigente pero el servidor no envía
   * nada hasta que pague. Ausente = false.
   */
  sending_paused: boolean;
  /** El enlace firmado para cambiar la tarjeta y cobrar. Solo con `sending_paused`. */
  pay_url: string | null;
}

export interface PartnerDiscount {
  code: string;
  /** Quién lo invitó, para el aviso de consentimiento junto al precio. Null sin nombre. */
  partner_name?: string | null;
  /** En centavos de USD. Por cuánto tiempo: `duration` (ver `discountMonths`). */
  amount_off: number;
  currency: string;
  duration: string | null;
  duration_in_months: number | null;
}

export const NO_PLAN: PlanState = {
  plan: null,
  plan_active: false,
  renews_at: null,
  demo_sent_at: null,
  trial_eligible: false,
  cta_case: null,
  trialing: false,
  trial_ends_at: null,
  can_manage_billing: false,
  partner_discount: null,
  sending_paused: false,
  pay_url: null,
};

function readPartnerDiscount(value: unknown): PartnerDiscount | null {
  const raw = asRecord(value);
  const amount = Number(raw.amount_off);
  if (typeof raw.code !== 'string' || !raw.code || !Number.isFinite(amount) || amount <= 0) return null;
  const name = typeof raw.partner_name === 'string' ? raw.partner_name.trim() : '';
  return {
    code: raw.code,
    partner_name: name || null,
    amount_off: amount,
    currency: typeof raw.currency === 'string' ? raw.currency : 'usd',
    duration: typeof raw.duration === 'string' ? raw.duration : null,
    duration_in_months:
      typeof raw.duration_in_months === 'number' && raw.duration_in_months > 0 ? raw.duration_in_months : null,
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function asText(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value;
  if (value instanceof Date) return value.toISOString();
  return null;
}

export function readPlan(source: unknown): PlanState {
  const root = asRecord(source);
  const membership = asRecord(root.membership);

  // La raíz manda; `membership` es el respaldo. `plan_active: false` es un valor
  // legítimo, así que la prueba es contra undefined/null y no contra falsy.
  const pick = (key: string): unknown =>
    root[key] !== undefined && root[key] !== null ? root[key] : membership[key];

  const plan = asText(pick('plan')) ?? asText(membership.subscription_plan);
  const active = pick('plan_active');
  const planActive =
    typeof active === 'boolean' ? active : membership.subscription_active === true;

  return {
    plan,
    plan_active: planActive,
    renews_at: asText(pick('renews_at')) ?? asText(membership.subscription_expires_at),
    demo_sent_at: asText(pick('demo_sent_at')),
    trial_eligible: pick('trial_eligible') === true,
    cta_case: ((v) => (v === 'trial' || v === 'reactivate' || v === 'activate' ? v : null))(pick('cta_case')),
    trialing: pick('trialing') === true,
    trial_ends_at: asText(pick('trial_ends_at')),
    can_manage_billing: pick('can_manage_billing') === true,
    partner_discount: readPartnerDiscount(root.partner_discount),
    sending_paused: pick('sending_paused') === true,
    pay_url: ((v) => (typeof v === 'string' && /^https:\/\//.test(v) ? v : null))(pick('pay_url')),
  };
}

/** El nombre que ve el candidato. El código crudo ('QUARTERLY') no le dice nada. */
export function planLabel(plan: string | null): string {
  const t = messages().plan.labels;
  if (!plan) return t.none;
  if (plan === 'MONTHLY') return monthlyLabel();
  if (plan === 'QUARTERLY') return t.quarterly;
  // Los planes viejos de pago único siguen vivos en cuentas que ya los compraron.
  return t.legacy(plan);
}

/** Una fecha en la forma en que la lee una persona, o cadena vacía si no hay. */
export function formatPlanDate(iso: string | null): string {
  // La forma larga en el idioma activo; vacía (no la raya) cuando no hay fecha.
  return formatLongDate(iso, '');
}

/**
 * La fecha en que el plan se acabó — SÓLO si ya pasó. Vacía si todavía no.
 *
 * `renews_at` es el fin del período pagado, y el backend puede declarar el plan
 * muerto ANTES de esa fecha: Stripe deja `current_period_end` en el futuro
 * también cuando la suscripción se canceló o murió por impago, y por eso
 * `plan_active` mira además el estado (`lib/subscription.ts` del servicio).
 *
 * Sin esta guarda, a quien cancelan el 28 de agosto le decimos "tu plan venció
 * el 14 de noviembre": una fecha que puede desmentir mirando el calendario,
 * justo en la pantalla donde le estamos pidiendo la tarjeta. Cuando la fecha no
 * sirve, la frase correcta es "ya no está vigente" — que es cierta siempre.
 */
export function formatEndedPlanDate(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  if (date.getTime() > now.getTime()) return '';
  return formatPlanDate(iso);
}
